import type { UIRouterReact } from "@uirouter/react/lib/core";
import _ from "lodash";

import { chosenItemsChange } from "app/actions/routingActions";
import { CHART_COLORS } from "app/constants/ChartColors";
import { fetchSimilarSitesQuery } from "app/services/sitesService/queries/sitesQueries";
import {
    getDomainWithSubdomain,
    getUrlComponents,
} from "pro-features/components/AutocompleteWebsites/urlComponentUtils";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import { store } from "single-spa/store/store";

import organicPaidService from "./organicPaid";

const $rootScope: any = {
    global: {
        site: {
            similarSites: [],
        },
        compare: {},
    },
};

export interface IPrimarySite {
    availableDates: Array<any>;
    category: string;
    categoryRanking: number;
    country: string;
    description: string;
    displayName: string;
    el_description: string;
    employeeRange: string;
    globalRanking: number;
    hasGaToken: boolean;
    highestTrafficCountry: {
        id: number;
        code: string;
        text: string;
        states: string[];
        children: string[];
    };
    highestTrafficCountryRanking: number;
    icon: string;
    image: string;
    imageLarge: string;
    isSubDomain: boolean;
    isVirtual: boolean;
    mainDomainName: string;
    monthlyVisits: number;
    name: string;
    privacyStatus: string;
    relatedApps: { apps_0?: string[]; apps_1?: string[] };
    relatedSites: { [key: string]: unknown };
    similarSites: string[];
    tags: string[];
    title: string;
    yearFounded: string;
}

const localVars: {
    listByColors?: { [key: string]: string };
    isFavorite?: boolean;
    site?: IPrimarySite;
    list?: any[];
} = {
    listByColors: {},
};
let _similarSites: FixmeType[] = [];
let _similarSitesData: FixmeType[] = [];

const init = (router: UIRouterReact) => {
    router.transitionService.onCreate({}, (transition) => {
        const to = transition.to();
        const toParams = transition.params();
        const fromParams = transition.params("from");

        if (proNavigator.validateParams(Object.assign({}, toParams), to)) {
            return;
        }
        if (toParams.key) {
            const keys = toParams.key.split(",");
            const pages = toParams.pageFilter ? JSON.parse(toParams.pageFilter) : [];
            // @ts-ignore FIXME: strict migration - 'localVars.site' possibly undefined — legacy initialization order assumes site exists by this point
            localVars.site.relatedApps = {};
            // @ts-ignore FIXME: strict migration - 'localVars.site' possibly undefined — legacy initialization order assumes site exists by this point
            localVars.site.relatedSites = {};
            if (pages.length > 0) {
                localVars.site = pages[0].url;
                localVars.list = pages.slice(1).map((page: FixmeType) => page.url);
            } else {
                localVars.site = keys[0];
                localVars.list = keys.splice(1);
            }

            // Check if key actually changed - only make API call if key changed
            // This prevents unnecessary calls when only pageFilter changes
            const fromKey = fromParams?.key;
            const hasKeyChanged = fromKey !== toParams.key;

            if (hasKeyChanged || !fromKey) {
                void fetchSimilarSitesQuery(keys[0]).then((data) => {
                    _similarSites = data && _.map(data, "Domain");
                    _similarSitesData = data;
                });
            }
        }
    });

    router.transitionService.onSuccess({}, (transition) => {
        if ($rootScope.global.compare) {
            $rootScope.global.compare.loadedLength = localVars.list?.length;
        }
    });
};

function notify() {
    store.dispatch(chosenItemsChange(sitelistForLegend()));
}

function onChange() {
    localVars.isFavorite = false;
    notify();
}

/**
 * debounce to end of task queue
 * avoids rerendering when both localVars.site and localVars.list are changed
 */
const onChangeDebounced = _.debounce(onChange, 0);

Object.defineProperties(localVars, {
    site: {
        set: (value) => {
            value = value && value.toLowerCase();
            const isVirtual = value ? value[0] == "*" : false;
            if ($rootScope.global && $rootScope.global.site) {
                $rootScope.global.site.name = value;
                $rootScope.global.site.isVirtual = isVirtual;
                $rootScope.global.site.displayName = isVirtual ? value.substring(1) : value;
            }
            onChangeDebounced();
        },
        get: () => ($rootScope.global && $rootScope.global.site) || {},
    },
    list: {
        set: function (value) {
            $rootScope.global && $rootScope.global.compare && ($rootScope.global.compare.list = value);
            // @ts-ignore FIXME: strict migration - 'localVars.listByColors' possibly undefined — initialized at declaration but TS can't track property descriptor 'this' binding
            localVars.listByColors[this.site.name] = CHART_COLORS.compareMainColors[0]; //similarweb.config.compareMainColors[0];
            value.forEach((item: FixmeType, index: number) => {
                // @ts-ignore FIXME: strict migration - 'localVars.listByColors' possibly undefined — initialized at declaration above
                localVars.listByColors[item] = CHART_COLORS.compareMainColors[index + 1]; //similarweb.config.compareMainColors[index. + 1];
            });
            onChangeDebounced();
        },
        get: () => ($rootScope.global && $rootScope.global.compare && $rootScope.global.compare.list) || [],
    },
});

function clear(toParams: FixmeType) {
    // clear site
    // @ts-ignore FIXME: strict migration - Type 'null' not assignable to 'IPrimarySite | undefined' — preserve legacy clear semantics (null vs undefined)
    localVars.site = null;
    localVars.list = [];
    _similarSites = [];
    _similarSitesData = [];
}

function isFavorite() {
    return localVars.isFavorite;
}

function similarSites() {
    return _similarSites;
}

function similarSitesData() {
    return _similarSitesData;
}

const listInfo: any = {};

// @todo: questionable behavior, should move it to directive
function formatSite(item: FixmeType) {
    const siteInfo = getInfo(item);
    return (
        '<img class="favicon" src="' + siteInfo.icon + '"><span class="site-name">' + siteInfo.displayName + "</span>"
    );
}

function getPrimarySite() {
    return localVars.site;
}

function isPrimaryVirtual() {
    // @ts-ignore FIXME: strict migration - 'localVars.site' possibly undefined — legacy invariant, caller ensures site exists
    return localVars.site.isVirtual;
}

function updateMainSite(data: FixmeType) {
    const mainSite = localVars.site?.name;
    // data can be null/undefined when the site info response body is empty (see transformSiteInfoResponse)
    if (data && mainSite && mainSite in data) {
        const siteInfo = data[mainSite];
        Object.assign($rootScope.global.site, siteInfo);
    }
    updateInfo(data);
}

function removeItem(item: FixmeType) {
    localVars.list = _.reject(localVars.list, (i) => i === item);
}

function isCompare() {
    // @ts-ignore FIXME: strict migration - 'localVars.list' possibly undefined — legacy invariant, list initialized via setter
    return !!localVars.list.length;
}

function execute(callbacks: FixmeType /* data1, data2, ..., dataN */) {
    let allData;
    if (arguments.length > 1) {
        // eslint-disable-next-line prefer-rest-params
        allData = Array.prototype.slice.call(arguments, 1);
    }

    if (callbacks.pre) {
        callbacks.pre();
    }

    if (isCompare() && callbacks.compare) {
        callbacks.compare.apply(null, allData);
    } else if (callbacks.single) {
        // @ts-ignore FIXME: strict migration - getPrimarySite() possibly undefined — legacy invariant, primary site exists when single path runs
        callbacks.single.apply(null, allData && _.map(allData, (data) => data[getPrimarySite().name]));
    }
    if (callbacks.post) {
        callbacks.post();
    }
}

function count() {
    // @ts-ignore FIXME: strict migration - 'localVars.list' possibly undefined — legacy invariant, list initialized via setter
    return localVars.list.length + ($rootScope.global.site ? 1 : 0);
}

function isLoaded() {
    return _.every([$rootScope.global.site.name].concat(localVars.list), (item) => item in listInfo);
}

function get(includeCompetitors?: boolean) {
    if (includeCompetitors === false) {
        // @ts-ignore FIXME: strict migration - No overload matches concat with 'any[] | undefined' — legacy invariant, list initialized
        return [].concat(localVars.list);
    } else {
        // @ts-ignore FIXME: strict migration - 'localVars.site' possibly undefined / concat overload — legacy invariant, site & list initialized
        return localVars.site.name ? [localVars.site.name].concat(localVars.list) : [];
    }
}

function getInfo(name: string) {
    return name in listInfo ? listInfo[name] : { name: name, displayName: name };
}

function getSitesByMainName() {
    const res: FixmeType[] = [];
    _.forEach(listInfo, (value) => res.push(value.mainDomainName));
    return res.join("~");
}

function getSiteColor(site: string) {
    // @ts-ignore FIXME: strict migration - 'localVars.listByColors' possibly undefined — initialized at declaration above
    return localVars.listByColors[site];
}

function registerSiteOrigins(records: FixmeType) {
    records.forEach((record: FixmeType) => {
        const siteOrigins: FixmeType[] = [];
        forEach((site: string, siteInfo: FixmeType, index: number) => {
            if (record.SiteOrigins[site]) {
                siteOrigins.push({
                    name: site,
                    value: record.SiteOrigins[site],
                    index: index,
                });
            }
        });
        record.SiteOrigins = siteOrigins;

        record.Share = {
            // @ts-ignore FIXME: strict migration - getPrimarySite() possibly undefined — legacy invariant, primary site exists in this flow
            name: getPrimarySite().name,
            value: record.Share,
        };

        if (record.Children) {
            registerSiteOrigins(record.Children);
        }
    });
}

function calcTotal(data: FixmeType, member: FixmeType) {
    let totalVisitsAll = 0;
    forEach((site: string) => {
        const siteData = data[site];
        if (!siteData) {
            return;
        }
        let sum = siteData[member];
        if (_.isArray(sum)) {
            sum = organicPaidService.reduce(sum);
        }
        totalVisitsAll += sum;
    });
    return totalVisitsAll;
}

function forEach(iterator: FixmeType, context = window) {
    get().forEach((site, index) => {
        iterator.call(context, site, getInfo(site), index);
    });
}

function map(iterator: FixmeType) {
    const res: FixmeType[] = [];
    get().forEach((site, index) => {
        const siteComponents = getUrlComponents(site);
        // @ts-ignore FIXME: strict migration - 'siteComponents.subdomain' possibly undefined — legacy code, getDomainWithSubdomain handles undefined
        const domainAndSubdomain = getDomainWithSubdomain(siteComponents.domain, siteComponents.subdomain);
        const siteInfo = getInfo(domainAndSubdomain);
        const item: { displayName: string } = iterator(site, siteInfo, index);
        if (_.isObject(item) && !item.displayName) {
            item.displayName = siteInfo.displayName;
        }
        res.push(item);
    });
    return res;
}

function updateInfo(siteInfoList: FixmeType) {
    _.forEach(siteInfoList, (item, key) => {
        listInfo[key] = {
            icon: item.icon,
            image: item.image,
            category: item.category,
            displayName: item.isVirtual ? key.substring(1) : key,
            isVirtual: item.isVirtual,
            mainDomainName: item.mainDomainName,
            name: item.isVirtual ? key.substring(1) : key,
            smallIcon: true,
        };
    });
    store.dispatch(chosenItemsChange(sitelistForLegend()));
}

function sitelistForLegend() {
    return map((name: string, info: FixmeType) => ({
        name: info.displayName,
        // @ts-ignore FIXME: strict migration - 'localVars.listByColors' possibly undefined — initialized at declaration above
        color: localVars.listByColors[name],
        icon: info.icon,
        image: info.image,
        smallIcon: true,
    }));
}

function registerVisits($scope: FixmeType, tableName: string, data: FixmeType) {
    const visitsList: FixmeType[] = [];

    forEach((site: string) => {
        const visit = {
            Display: site,
            Name: formatSite(site),
            Value: data.TotalVisitsGlobalList[site],
        };
        visitsList.push(visit);
    });

    $scope[tableName].visitsList = visitsList;
}

function registerOPS($scope: FixmeType, tableName: string, data: FixmeType) {
    const ops: FixmeType[] = [];

    forEach((site: string) => {
        const organic = _.find(data.OP[site], (item) => item.Name === "0"),
            paid = _.find(data.OP[site], (item) => item.Name === "1"),
            organicValue = (organic && organic.Value) || 0,
            paidValue = (paid && paid.Value) || 0,
            sum = paidValue + organicValue,
            op = {
                Name: formatSite(site),
                Site: site,
                Value: [
                    ["Organic", organicValue / sum, 0],
                    ["Paid", paidValue / sum, 1],
                ],
            };

        ops.push(op);
    });

    $scope[tableName].opsList = ops;
}

function registerLists($scope: FixmeType, items: FixmeType, tableName: string, data: FixmeType) {
    function parseOthersData(TotalVisitsGlobalList: FixmeType, sum: number, name: string) {
        if (Array.isArray(TotalVisitsGlobalList)) {
            return data.TotalVisitsGlobalList.find((site: FixmeType) => site.name === name).percentage === 0
                ? 0
                : 1 - sum;
        }
        return data.TotalVisitsGlobalList[name] === 0 ? 0 : 1 - sum;
    }

    const columns: FixmeType[] = [],
        // @ts-ignore FIXME: strict migration - getPrimarySite() possibly undefined — legacy invariant, primary site exists in this flow
        mainSite = getPrimarySite().name;

    forEach((site: string) => {
        columns.push(formatSite(site));
    });

    _.forEach(items, (item) => {
        const list: FixmeType[] = [],
            name = item.name,
            sums: Record<string, number> = {},
            topList = window.similarweb.utils.formatTopList(item.topList || data[name][mainSite] || [], 7); //sort by given toplist or main site

        let hasOthers = false;
        if (
            topList.length &&
            _.intersection(["other", "others"], [topList[topList.length - 1].Name.toLowerCase()]).length
        ) {
            hasOthers = true;
            topList.splice(-1, 1);
        }

        _.forEach(topList, function (category) {
            const categorySites = (
                item.func ||
                function (i: FixmeType) {
                    return i;
                }
            )(
                {
                    name: category.Name,
                    sites: [],
                },
                category,
            );

            forEach((site: string) => {
                const res = _.find(data[name][site], (item) => item.Name === category.Name);

                categorySites.sites.push({
                    name: site,
                    value: res ? res.Value : 0,
                });
            });

            list.push(categorySites);
        });

        _.forEach(list, (categories) => {
            _.forEach(categories.sites, (site: FixmeType) => {
                sums[site.name] = sums[site.name] || 0;
                sums[site.name] += Math.round(10000 * site.value) / 10000;
            });
        });

        if (hasOthers) {
            list.push({
                name: "Others",
                sites: _.map(sums, (sum, name) => ({
                    name,
                    value: parseOthersData(data.TotalVisitsGlobalList, sum, name),
                })),
            });
        }

        $scope[tableName].columns = columns;
        $scope[tableName][name + "List"] = list;
    });
}

export const chosenSites = {
    similarSites: similarSites,
    similarSitesData: similarSitesData,
    listInfo: listInfo,
    formatSite: formatSite,
    getPrimarySite: getPrimarySite,
    isPrimaryVirtual: isPrimaryVirtual,
    updateMainSite: updateMainSite,
    removeItem: removeItem,
    isCompare: isCompare,
    execute: execute,
    count: count,
    isLoaded: isLoaded,
    get: get,
    getInfo: getInfo,
    getSitesByMainName: getSitesByMainName,
    getSiteColor: getSiteColor,
    registerSiteOrigins: registerSiteOrigins,
    calcTotal: calcTotal,
    forEach: forEach,
    map: map,
    updateInfo: updateInfo,
    sitelistForLegend: sitelistForLegend,
    registerVisits: registerVisits,
    registerOPS: registerOPS,
    registerLists: registerLists,
    isFavorite: isFavorite,
    clear: clear,
    init: init,
};
